import React, { createContext, Dispatch, ReactNode, useContext, useReducer } from 'react';

import { cloneDeep, isUndefined } from 'lodash';

import { Profile, useProfile } from 'auth/Auth';
import { Comment } from 'common/comments2/Comments2.types';
import { useConfig } from 'core/util/Config';
import { Route } from 'core/util/Route';
import { DEFAULT_SELECTED_CHART } from 'reviews2/feed/metrics/Metrics.util';
import { DEFAULT_SELECTED_FILTER } from 'reviews2/feed/reviews-filter/ReviewsFilter';
import { Filter } from 'reviews2/feed/reviews-filter/ReviewsFilter.types';
import { DEFAULT_SELECTED_SORT } from 'reviews2/feed/reviews-sort/ReviewsSort';
import { Sort } from 'reviews2/feed/reviews-sort/ReviewsSort.types';
import { YetiReview } from 'reviews2/feed/Reviews.types';

const DEFAULT_REVIEWS_COUNT = 10;

interface ReviewsContainer {
    reviews: Comment[];
    reviewsYeti?: YetiReview[];
    currentReview?: Comment;
    currentYetiReview?: YetiReview;
    selectedReviewsFilter: Filter;
    selectedReviewsSort: Sort;
    currentPage: number;
    currentCount: number;
    currentStart: number;
    currentEnd: number;
    profile?: Profile;
    defaultFeed: string; // Reviews, Unified Reviews or Reviews3
    currentChart: number;
    generatedId?: string;
    reviewsPerPage?: number;
    isNorthStarEnabled?: boolean;
    showMetrics?: boolean;
}

interface ReviewsProviderProps {
    children: ReactNode;
    isNorthStarEnabled?: boolean;
}

export enum ReviewsActionType {
    SetReviews,
    SetYetiReviews,
    SetCurrentReview,
    SetSelectedReviewsFilter,
    SetSelectedReviewsSort,
    SetCurrentStart,
    SetCurrentEnd,
    SetReviewsCount,
    SetCurrentPage,
    SetDefaultFeed,
    SetCurrentChart,
    SetReviewsPerPage,
    SetNorthStarEnabled,
    SetShowMetrics,
}

export interface ReviewsAction {
    type: ReviewsActionType;
    review?: Comment;
    reviews?: Comment[];
    selectedSort?: Sort;
    selectedFilter?: Filter;
    currentStart?: number;
    currentCount?: number;
    currentPage?: number;
    currentEnd?: number;
    defaultFeed?: string;
    currentChart?: number;
    generatedId?: string;
    reviewsPerPage?: number;
    isNorthStarEnabled?: boolean;
    showMetrics?: boolean;
}

const ReviewsStateContext = createContext<ReviewsContainer | undefined>(undefined);
const ReviewsDispatchContext = createContext<Dispatch<ReviewsAction> | undefined>(undefined);

const getReviewsFilterKey = (profile: Profile): string => {
    return `r4e-reviews-filter-${profile.user.id}-${profile.user.locale}`;
};

const getReviewsSortKey = (profile: Profile): string => {
    return `r4e-reviews-sort-${profile.user.id}-${profile.user.locale}`;
};

export const getReviewsDefaultFeedKey = (profile: Profile): string => {
    return `r4e-reviews-default-feed-${profile.user.id}-${profile.user.locale}`;
};

const getReviewsChartKey = (profile: Profile): string => {
    return `r4e-reviews-chart-${profile.user.id}-${profile.user.locale}`;
};

export const getReviewsPerPageKey = (profile: Profile): string => {
    return `r4e-reviews-per-page-${profile.user.id}-${profile.user.locale}`;
};

const reviewsReducer = (reviewsContainer: ReviewsContainer, action: ReviewsAction): ReviewsContainer => {
    switch (action.type) {
        case ReviewsActionType.SetReviews: {
            if (!action.reviews) {
                throw new TypeError('SetReviews: required action arg missing: reviews');
            }

            return { ...reviewsContainer, reviews: action.reviews };
        }
        case ReviewsActionType.SetCurrentReview: {
            // no check: current review can be undefined
            return { ...reviewsContainer, currentReview: action.review };
        }
        case ReviewsActionType.SetSelectedReviewsFilter: {
            if (!action.selectedFilter) {
                throw new TypeError('SetSelectedReviewsFilter: required action arg missing: selectedFilter');
            }

            localStorage.setItem(getReviewsFilterKey(reviewsContainer.profile!), JSON.stringify(action.selectedFilter));

            return { ...reviewsContainer, selectedReviewsFilter: action.selectedFilter };
        }
        case ReviewsActionType.SetSelectedReviewsSort: {
            if (!action.selectedSort) {
                throw new TypeError('SetSelectedReviewsSort: require action arg missing: selectedSort');
            }

            localStorage.setItem(getReviewsSortKey(reviewsContainer.profile!), JSON.stringify(action.selectedSort));

            return { ...reviewsContainer, selectedReviewsSort: action.selectedSort };
        }
        case ReviewsActionType.SetReviewsCount: {
            if (isUndefined(action.currentCount)) {
                throw new Error('SetReviewsCount: required action arg missing: currentCount');
            }

            return { ...reviewsContainer, currentCount: action.currentCount };
        }
        case ReviewsActionType.SetCurrentPage: {
            if (isUndefined(action.currentPage)) {
                throw new Error('SetCurrentPage: required action arg missing: currentPage');
            }

            return { ...reviewsContainer, currentPage: action.currentPage };
        }
        case ReviewsActionType.SetCurrentStart: {
            if (isUndefined(action.currentStart)) {
                throw new Error('SetCurrentStart: required action arg missing: currentStart');
            }

            return { ...reviewsContainer, currentStart: action.currentStart };
        }
        case ReviewsActionType.SetCurrentEnd: {
            if (isUndefined(action.currentEnd)) {
                throw new Error('SetCurrentEnd: required action arg missing: currentEnd');
            }

            return { ...reviewsContainer, currentEnd: action.currentEnd };
        }
        case ReviewsActionType.SetDefaultFeed: {
            if (isUndefined(action.defaultFeed)) {
                throw new Error('SetDefaultFeed: required action arg missing: defaultFeed');
            }

            localStorage.setItem(
                getReviewsDefaultFeedKey(reviewsContainer.profile!),
                JSON.stringify(action.defaultFeed),
            );

            return { ...reviewsContainer, defaultFeed: action.defaultFeed, generatedId: action.generatedId };
        }
        case ReviewsActionType.SetCurrentChart: {
            if (isUndefined(action.currentChart)) {
                throw new Error('SetCurrentChart: required action arg missing: currentChart');
            }

            localStorage.setItem(getReviewsChartKey(reviewsContainer.profile!), JSON.stringify(action.currentChart));

            return { ...reviewsContainer, currentChart: action.currentChart };
        }
        case ReviewsActionType.SetYetiReviews: {
            if (!action.reviews) {
                throw new TypeError('SetYetiReviews: required action arg missing: reviews');
            }

            return { ...reviewsContainer, reviewsYeti: action.reviews };
        }
        case ReviewsActionType.SetReviewsPerPage: {
            if (isUndefined(action.reviewsPerPage)) {
                throw new Error('SetReviewsPerPage: required action arg missing: reviewsPerPage');
            }

            localStorage.setItem(
                getReviewsPerPageKey(reviewsContainer.profile!),
                JSON.stringify(action.reviewsPerPage),
            );

            return { ...reviewsContainer, reviewsPerPage: action.reviewsPerPage };
        }
        case ReviewsActionType.SetNorthStarEnabled: {
            if (isUndefined(action.isNorthStarEnabled)) {
                throw new Error('SetNorthStarEnabled: required action arg missing: isNorthStarEnabled');
            }

            return { ...reviewsContainer, isNorthStarEnabled: action.isNorthStarEnabled };
        }
        case ReviewsActionType.SetShowMetrics: {
            if (isUndefined(action.showMetrics)) {
                throw new Error('SetShowMetrics: required action arg missing: showMetrics');
            }

            return { ...reviewsContainer, showMetrics: action.showMetrics };
        }
        default:
            throw new Error(`Reviews: unhandled action type: ${action.type}`);
    }
};

export const ReviewsProvider = ({ children, isNorthStarEnabled }: ReviewsProviderProps): JSX.Element => {
    const [{ profile }] = useProfile();
    const [{ config }] = useConfig();
    const reviewsFilterKey = getReviewsFilterKey(profile!);
    const reviewsSortKey = getReviewsSortKey(profile!);
    const reviewsDefaultFeedKey = getReviewsDefaultFeedKey(profile!);
    const reviewsChartKey = getReviewsChartKey(profile!);
    const reviewsPerPageKey = getReviewsPerPageKey(profile!);
    const DEFAULT_FEED = `${config?.urls?.app}/${Route.Reviews3}`;

    const [state, dispatch] = useReducer(reviewsReducer, {
        profile,
        reviews: [],
        currentPage: 0,
        currentCount: 0,
        currentStart: 0,
        currentEnd: 0,
        showMetrics: false,
        isNorthStarEnabled,
        reviewsPerPage: localStorage.getItem(reviewsPerPageKey)
            ? cloneDeep(JSON.parse(localStorage.getItem(reviewsPerPageKey)!))
            : DEFAULT_REVIEWS_COUNT,
        currentChart: localStorage.getItem(reviewsChartKey)
            ? cloneDeep(JSON.parse(localStorage.getItem(reviewsChartKey)!))
            : DEFAULT_SELECTED_CHART,
        defaultFeed: localStorage.getItem(reviewsDefaultFeedKey)
            ? cloneDeep(JSON.parse(localStorage.getItem(reviewsDefaultFeedKey)!))
            : DEFAULT_FEED,
        selectedReviewsFilter: localStorage.getItem(reviewsFilterKey)
            ? cloneDeep(JSON.parse(localStorage.getItem(reviewsFilterKey)!))
            : DEFAULT_SELECTED_FILTER,
        selectedReviewsSort: localStorage.getItem(reviewsSortKey)
            ? cloneDeep(JSON.parse(localStorage.getItem(reviewsSortKey)!))
            : DEFAULT_SELECTED_SORT,
    });

    return (
        <ReviewsStateContext.Provider value={state}>
            <ReviewsDispatchContext.Provider value={dispatch}>{children}</ReviewsDispatchContext.Provider>
        </ReviewsStateContext.Provider>
    );
};

export const useReviewsState = (): ReviewsContainer => {
    const context = useContext(ReviewsStateContext);

    if (context === undefined) {
        throw new Error('useReviewsState must be used within a ReviewsProvider');
    }

    return context;
};

export const useReviewsDispatch = (): Dispatch<ReviewsAction> => {
    const context = useContext(ReviewsDispatchContext);

    if (context === undefined) {
        throw new Error('useReviewsDispatch must be used within a ReviewsProvider');
    }

    return context;
};

export const useReviews = (): [ReviewsContainer, Dispatch<ReviewsAction>] => {
    return [useReviewsState(), useReviewsDispatch()];
};
